import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { GraduationCap, MapPin } from "lucide-react";
import { Chapter, Reveal } from "@/components/Chapter";
import { useContent } from "@/content";

export const About = () => {
  const { COMPANY_COPY, FOUNDER } = useContent();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
  <section id="about" className="relative max-w-7xl mx-auto px-4 sm:px-8 py-24 md:py-32" data-testid="about-section">
    <div className="glow-amber-light absolute top-40 -left-40 h-[420px] w-[420px] rounded-full pointer-events-none" />
    <Chapter
      id="06"
      name="About"
      title="A technology partner built on accountable, on-time work."
    />

    <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
      <div>
        {COMPANY_COPY.map((p, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6" data-testid={`about-company-copy-${i}`}>{p}</p>
          </Reveal>
        ))}

        <Reveal delay={0.2}>
          <div ref={imgRef} className="relative mt-10 rounded-2xl overflow-hidden border border-slate-200 shadow-lg shadow-slate-900/5 group" data-testid="about-image-frame">
            <motion.img
              style={{ y: imgY, scale: 1.18 }}
              src="/assets/about-team.jpg"
              alt="The Desantrix team"
              className="w-full h-64 object-cover saturate-[0.75] group-hover:saturate-100 transition-[filter] duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1528]/60 via-transparent to-transparent" />
            <span className="absolute bottom-4 left-5 font-mono text-[10px] tracking-[0.3em] uppercase text-amber-300">Serving Clients Worldwide</span>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <motion.figure
          whileHover={{ y: -6 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          className="relative rounded-2xl border border-slate-200 bg-white p-8 md:p-10 shadow-sm"
          data-testid="founder-card"
        >
          <span className="absolute -top-5 left-8 font-extrabold text-7xl text-amber-400/25 select-none">"</span>
          <blockquote className="space-y-5">
            {FOUNDER.bio.map((p, i) => (
              <p key={i} className="text-sm sm:text-base text-slate-600 leading-relaxed" data-testid={`founder-bio-${i}`}>{p}</p>
            ))}
          </blockquote>
          <figcaption className="mt-8 pt-6 border-t border-slate-100">
            <p className="text-lg font-semibold text-[#0B1528]" data-testid="founder-name">{FOUNDER.name}</p>
            <p className="text-sm text-amber-600 mt-0.5" data-testid="founder-role">{FOUNDER.role}</p>
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5" data-testid="founder-degree">
                <GraduationCap className="h-3.5 w-3.5 text-amber-500" />
                {FOUNDER.degree}
              </span>
              <span className="inline-flex items-center gap-1.5" data-testid="founder-location">
                <MapPin className="h-3.5 w-3.5 text-amber-500" />
                {FOUNDER.location}
              </span>
            </div>
          </figcaption>
        </motion.figure>
      </Reveal>
    </div>
  </section>
  );
};
